import React, { useEffect, useMemo, useState } from 'react';
import { Card, Title, Text, Stack, Group, Select, Button, Loader, MultiSelect, ActionIcon, Tooltip, Checkbox, Collapse, SimpleGrid, ColorInput, Progress, Center } from '@mantine/core';
import DataChartSettingsDialog from '../components/data/DataChartSettingsDialog';
import DataTableSection from '../components/data/DataTableSection';
import DataChartSection from '../components/echarts/DataChartSection';
import MetricChartBlock from '../components/data/MetricChartBlock';
import { useTranslation } from 'react-i18next';
import { fetchMeasurements, fetchSeries, listMetricDefs, fetchWeatherSeries, searchSensors } from '../services/dataService';
import { useDeviceFilter } from '../hooks/useDeviceFilter';
import type { DevicesDirectory, DeviceRef, MetricDef } from '../types';
import { useMediaQuery, useLocalStorage } from '@mantine/hooks';
import type { DataQuery, MeasurementRecord, SeriesPoint } from '../types';
import type { PivotedMeasurementRecord } from '../types/pivoted';
import { computeIntervalOptionsFromRange } from '../utils/date';
import type { WeatherExtraMetric } from '../services/dataService';
import { IconSettings } from '@tabler/icons-react';
import { DateTimePicker } from '@mantine/dates';
import { useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';

// Debounce utility function
function debounce<T extends (...args: string[]) => void | Promise<void>>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

const DataPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  
  // Device filter hook (replaces manual devicesDir state)
  const deviceFilter = useDeviceFilter({
    initialGateways: searchParams.getAll('gatewayId'),
    initialSensors: searchParams.getAll('sensorId'),
  });
  
  const [metricDefs, setMetricDefs] = useState<MetricDef[]>([]);
  const [initLoaded, setInitLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PivotedMeasurementRecord[]>([]);
  const [total, setTotal] = useState(0);
  // Default to last 1 month for balanced initial load
  const now = useMemo(() => new Date(), []);
  const oneMonthAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  }, []);
  const [query, setQuery] = useState<DataQuery>({
    page: 1,
    pageSize: 10,
    from: oneMonthAgo.toISOString(),
    to: now.toISOString(),
  });
  const isMobile = useMediaQuery('(max-width: 48em)');
  // Tablet-ish range around 992px to keep columns on desktop and break earlier when needed
  const isTablet = useMediaQuery('(max-width: 62em)');
  const [gatewaySearch, setGatewaySearch] = useState('');
  const [sensorSearch, setSensorSearch] = useState('');
  const [sensorLoading, setSensorLoading] = useState(false);
  
  // Alias for backward compatibility
  const devicesDir = deviceFilter.devicesDir;
  const gatewayOptions = deviceFilter.gatewayOptions;
  const sensorOptions = deviceFilter.sensorOptions;
  const sensorValueTypeById = deviceFilter.sensorValueTypeById;
  const sensorValueTypeNameById = deviceFilter.sensorValueTypeNameById;
  
  // Get filtered sensor IDs from the device filter hook
  const filteredSensorIds = useMemo(() => 
    deviceFilter.filteredSensors.map(s => s.id),
    [deviceFilter.filteredSensors]
  );
  // Mobile filter panel state: default based on initial viewport, do not toggle on resize to avoid flicker
  const [filtersOpen, setFiltersOpen] = useState<boolean>(true);
  const [chartSettingsOpen, setChartSettingsOpen] = useState<boolean>(false);
  const [seriesColors, setSeriesColors] = useState<Record<string, string>>({});

  // Chart state (OHLC)
  const [chartMetric, setChartMetric] = useState<string>('');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [seriesItems, setSeriesItems] = useState<SeriesPoint[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [metricSettings, setMetricSettings] = useState<Record<string, { color: string; axis: 'left' | 'right' | 'both'; type: 'line' | 'boxplot' | 'step' | 'arearange'; trendColor?: string; showTrend?: boolean }>>({});
  // Interval state for series resolution
  const [interval, setInterval] = useState<string>('30m');
  // Split chart options
  const [splitByMetric, setSplitByMetric] = useState<boolean>(false);
  const [splitByDevice, setSplitByDevice] = useState<boolean>(false);
  const [splitByGateway, setSplitByGateway] = useState<boolean>(false);

  // Zusatz-Metriken (Wetter)
  const [weatherMetrics, setWeatherMetrics] = useState<WeatherExtraMetric[]>([]);
  const [weatherPlace, setWeatherPlace] = useState<{ label: string; latitude: number; longitude: number } | null>(null);
  // Shape preferences for weather overlays (line | step | bar), persisted
  const [weatherShapes, setWeatherShapes] = useLocalStorage<Record<string, 'line' | 'step' | 'bar'>>({ key: 'tt_weather_shapes', defaultValue: {} });

  // Axis side preferences per unit (persisted)
  const [unitAxisSides, setUnitAxisSides] = useLocalStorage<Record<string, 'left' | 'right'>>({ key: 'tt_unit_axis_sides', defaultValue: {} });

  // Trendlines (persisted) - now per-line in metricSettings
  const [showTrends, setShowTrends] = useLocalStorage<boolean>({ key: 'tt_trends_show', defaultValue: false });
  // Trendziel ist fest auf 'base' (nur Basis-Serien) gesetzt
  const trendTargetsConst = 'base' as const;
  
  // Calculate if any trendline is enabled
  const hasAnyTrendline = Object.values(metricSettings).some(s => s.showTrend);

  // Debounced search handlers (gateway search now handled by hook)

  const debouncedSensorSearch = React.useCallback(
    debounce(async (searchTerm: string) => {
      setSensorLoading(true);
      try {
        const selectedGateway = Array.isArray(query.gatewayId) ? query.gatewayId[0] : query.gatewayId;
        const items = await searchSensors(searchTerm, 2000, selectedGateway);
        // Note: sensor options are now managed by the hook, but we keep this for search functionality
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Fehler',
          message: error instanceof Error ? error.message : 'Sensoren konnten nicht geladen werden.',
        });
      } finally {
        setSensorLoading(false);
      }
    }, 300),
    [query.gatewayId]
  );

  // Helpers: translate metric id via metricDefs and i18n
  const metricDefById = useMemo(() => Object.fromEntries(metricDefs.map((d) => [d.id, d])), [metricDefs]);

  // Map metric ID (from AssetValueType via ASSET_VALUE_TYPE_METRIC_MAP) → AssetValueType.name
  const metricIdToValueTypeName = useMemo(() => {
    const map = new Map<string, string>();
    (devicesDir?.sensors || []).forEach((s) => {
      if (s.metricId && s.valueTypeName) {
        map.set(s.metricId, s.valueTypeName);
      }
    });
    return map;
  }, [devicesDir]);

  const translateMetricId = React.useCallback((id: string): string => {
    // Prefer AssetValueType.name from the actual sensor assets
    const valueTypeName = metricIdToValueTypeName.get(id);
    if (valueTypeName) return valueTypeName;
    const def = metricDefById[id];
    if (!def) {
      // Fallback to direct i18n key (for weather.* etc.)
      const direct = t(id);
      return typeof direct === 'string' && direct !== id ? direct : id;
    }
    return (t(def.tKey) as string) || id;
  }, [metricDefById, metricIdToValueTypeName, t]);
  const unitForMetricId = React.useCallback((id: string): string => {
    const def = metricDefById[id];
    return (def?.unit ?? '').trim();
  }, [metricDefById]);
  const unitForAnyMetric = React.useCallback((id: string): string => {
    // Weather overrides
    if (id === 'weather.temperature') return '°C';
    if (id === 'weather.humidity') return '%';
    if (id === 'weather.precipitation') return 'mm';
    // Backend-defined
    const u = unitForMetricId(id);
    if (u) return u;
    // Fallback heuristics
    const b = id.toLowerCase();
    if (b.includes('humidity') || b.includes('feucht') || b.includes('batter')) return '%';
    if (b.includes('temp')) return '°C';
    if (b.includes('press')) return 'hPa';
    if (b.includes('voltage') || b.includes('volt')) return 'V';
    if (b.includes('current') || b.includes('amp')) return 'A';
    if (b.includes('precip') || b.includes('rain')) return 'mm';
    return '';
  }, [unitForMetricId]);

  // Compute current available units from active selections (after helpers are defined)
  const availableUnits = useMemo(() => {
    const base = (selectedMetrics.length ? selectedMetrics : (chartMetric ? [chartMetric] : []));
    const labels = [...base, ...weatherMetrics];
    const setU = new Set<string>();
    labels.forEach((id) => { const u = unitForAnyMetric(id); if (u) setU.add(u); });
    return Array.from(setU);
  }, [selectedMetrics, chartMetric, weatherMetrics, unitForAnyMetric]);
  const sensorNameById = useMemo(() => {
    const map = new Map<string, string>();
    (devicesDir?.sensors || []).forEach((s) => { if (s.name) map.set(s.id, s.name); });
    (devicesDir?.gateways || []).forEach((g) => { if (g.name) map.set(g.id, g.name); });
    return map;
  }, [devicesDir]);

  const sensorMetricIdById = useMemo(() => {
    const map = new Map<string, string>();
    (devicesDir?.sensors || []).forEach((s) => { if (s.metricId) map.set(s.id, s.metricId); });
    return map;
  }, [devicesDir]);

  const translateSeriesLabel = React.useCallback((label: string): string => {
    const hasSuffix = label.includes(' (');
    const base = hasSuffix ? label.substring(0, label.indexOf(' (')) : label;
    const rawSuffix = hasSuffix ? label.substring(label.indexOf(' (') + 2, label.lastIndexOf(')')) : '';
    // Use valueTypeName for the metric label
    const valueTypeName = rawSuffix ? sensorValueTypeNameById.get(rawSuffix) : undefined;
    const metricLabel = valueTypeName ?? translateMetricId(base);
    const displaySuffix = rawSuffix ? ` (${sensorNameById.get(rawSuffix) ?? rawSuffix})` : '';
    return `${metricLabel}${displaySuffix}`;
  }, [translateMetricId, sensorNameById, sensorValueTypeNameById]);

  // For axis labeling: use date-only when selected range > 2 days
  const dateOnlyTicks = useMemo(() => {
    try {
      const from = query.from ? Date.parse(String(query.from).replace(' ', 'T')) : NaN;
      const to = query.to ? Date.parse(String(query.to).replace(' ', 'T')) : NaN;
      if (!Number.isFinite(from) || !Number.isFinite(to)) return false;
      return (to - from) > 2 * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }, [query.from, query.to]);

  function getRangeMs(): number {
    const from = query.from ? Date.parse(String(query.from).replace(' ', 'T')) : undefined;
    const to = query.to ? Date.parse(String(query.to).replace(' ', 'T')) : undefined;
    if (!from || !to) return 1000 * 60 * 60 * 24; // default 1d
    return Math.max(0, to - from);
  }

  const computeIntervalOptions = (): { value: string; label: string }[] => {
    return computeIntervalOptionsFromRange(getRangeMs());
  };

  // Keep interval options sensible when range changes
  const intervalOptions = useMemo(() => computeIntervalOptions(), [query.from, query.to]);
  useEffect(() => {
    // ensure current interval is within the available options, otherwise set default
    if (!intervalOptions.some((o) => o.value === interval)) {
      setInterval(intervalOptions[0]?.value || '1h');
    }
  }, [intervalOptions]);

  // Init: metric defs only (device directory now handled by hook)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setLoadingProgress(0);
      setLoadingStage(t('common.loading.metrics') || 'Loading metrics...');
      
      try {
        // Stage 1: Load metric definitions
        setLoadingProgress(20);
        const defsRes = await listMetricDefs();

        if (cancelled) return;

        setMetricDefs(defsRes || []);
        setLoadingProgress(40);
        setLoadingStage(t('common.loading.devices') || 'Loading devices...');
        
        // Stage 2: Device directory is handled by hook, wait for it
        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay to show progress
        setLoadingProgress(60);
        setLoadingStage(t('common.loading.configuration') || 'Loading configuration...');
        
        // Stage 3: Read URL params and localStorage
        const gw = searchParams.getAll('gatewayId');
        const sn = searchParams.getAll('sensorId');
        setQuery((q) => ({
          ...q,
          gatewayId: gw.length ? gw : undefined,
          sensorId: sn.length ? sn : undefined,
          page: 1,
        }));

        setLoadingProgress(80);
        setLoadingStage(t('common.loading.preferences') || 'Loading preferences...');

        // Load weather selections from localStorage
        try {
          const wm = localStorage.getItem('tt_weather_metrics');
          if (wm) setWeatherMetrics(JSON.parse(wm));
          const wp = localStorage.getItem('tt_weather_place');
          if (wp) {
            const o = JSON.parse(wp);
            if (o && typeof o === 'object' && 'label' in o && 'latitude' in o && 'longitude' in o) {
              setWeatherPlace(o);
            }
          }
        } catch (e) {
          // Ignore localStorage errors
        }
        
        // Stage 4: Final setup
        setLoadingProgress(100);
        setLoadingStage(t('common.loading.complete') || 'Complete');
        
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) {
          setInitLoaded(true);
          setLoading(false);
          setLoadingProgress(0);
          setLoadingStage('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);
  
  // Set initial metrics from valueTypeName when device directory loads
  useEffect(() => {
    if (deviceFilter.devicesDir && selectedMetrics.length === 0) {
      const valueTypes = deviceFilter.valueTypeOptions.map(opt => opt.value);
      const preferred = valueTypes.find(vt => vt.toLowerCase().includes('humidity')) || valueTypes[0];
      if (preferred) {
        setSelectedMetrics([preferred]);
        setChartMetric(preferred);
      }
    }
  }, [deviceFilter.devicesDir, deviceFilter.valueTypeOptions]);

  // Persist weather settings
  useEffect(() => {
    try { localStorage.setItem('tt_weather_metrics', JSON.stringify(weatherMetrics)); } catch { /* Ignore */ }
  }, [weatherMetrics]);

  // React to weatherPlace changes made on SettingsPage (cross-tab or same-tab storage events)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== 'tt_weather_place') return;
      try {
        const o = e.newValue ? JSON.parse(e.newValue) : null;
        if (o && typeof o === 'object' && 'label' in o && 'latitude' in o && 'longitude' in o) {
          setWeatherPlace(o);
        } else {
          setWeatherPlace(null);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Basic validation: require valid date range
    if (!initLoaded) {
      return () => { cancelled = true; };
    }
    const fromMs = query.from ? Date.parse(String(query.from).replace(' ', 'T')) : NaN;
    const toMs = query.to ? Date.parse(String(query.to).replace(' ', 'T')) : NaN;
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs > toMs) {
      setError('Ungültiger Zeitraum');
      notifications.show({ color: 'red', title: t('common.error') || 'Fehler', message: t('data.validation.invalidRange') || 'Bitte gültigen Zeitraum wählen.' });
      return () => { cancelled = true; };
    }

    // Use filtered sensor IDs from hook if any filters are active
    const effectiveSensorIds = filteredSensorIds.length > 0 ? filteredSensorIds : 
      (Array.isArray(query.sensorId) ? query.sensorId : (query.sensorId ? [query.sensorId] : undefined));

    setLoading(true);
    setError(null);
    fetchMeasurements({ 
      ...query, 
      sensorId: effectiveSensorIds,
      gatewayId: undefined,
      metric: undefined
    })
      .then((res) => {
        if (cancelled) return;
        setData(res.items);
        setTotal(res.total);
        if ((res.items?.length ?? 0) === 0) {
          notifications.show({ color: 'yellow', title: t('common.info') || 'Hinweis', message: t('data.info.noData') || 'Keine Daten im gewählten Zeitraum.' });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(String(e));
          notifications.show({ color: 'red', title: t('common.error') || 'Fehler', message: String(e) });
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [query, selectedMetrics, chartMetric, initLoaded, filteredSensorIds]);

  // Fetch OHLC series for chart (supports multiple sensors by creating distinct series per sensor)
  // Note: Sensor filtering by gateway is now handled by the useDeviceFilter hook

  useEffect(() => {
    let cancelled = false;
    if (!initLoaded) {
      return () => { cancelled = true; };
    }
    const normIso = (val?: string) => {
      if (!val) return undefined;
      const ms = Date.parse(val.replace(' ', 'T'));
      if (!Number.isFinite(ms)) return undefined;
      return new Date(ms).toISOString();
    };
    const from = normIso(query.from);
    const to = normIso(query.to);
    const metricsParam = (selectedMetrics && selectedMetrics.length > 0)
      ? selectedMetrics
      : (chartMetric ? [chartMetric] : []);
    // Map interval to bucket + stepMs (minutes/hours) or monthly handling
    const stepMs = (() => {
      const m = 60 * 1000; const h = 60 * m; const d = 24 * h;
      if (interval.endsWith('m')) return parseInt(interval) * m;
      if (interval.endsWith('h')) return parseInt(interval) * h;
      if (interval.endsWith('mo')) return 30 * d; // approx, backend will use true months
      return 30 * m;
    })();
    const bucket: 'minute' | 'hour' = stepMs < 60 * 60 * 1000 ? 'minute' : 'hour';
    const rangeMs = (() => {
      const f = from ? Date.parse(from) : undefined;
      const t = to ? Date.parse(to) : undefined;
      return f && t ? Math.max(0, t - f) : (60 * 60 * 1000 * 24);
    })();
    // Points calculation; for monthly resolution, compute by month difference
    let points: number;
    if (interval.endsWith('mo') && from && to) {
      const monthsStep = Math.max(1, parseInt(interval.replace('mo', '')) || 1);
      const f = new Date(from);
      const t = new Date(to);
      const totalMonths = (t.getFullYear() - f.getFullYear()) * 12 + (t.getMonth() - f.getMonth()) + 1; // inclusive
      points = Math.max(1, Math.ceil(totalMonths / monthsStep));
    } else {
      points = Math.max(1, Math.ceil(rangeMs / stepMs));
    }
    const MAX_POINTS = 400;
    if (points > MAX_POINTS) points = MAX_POINTS;
    const gatewayIds: string[] = Array.isArray(query.gatewayId)
      ? query.gatewayId
      : (query.gatewayId ? [query.gatewayId] : []);
    // Use filtered sensor IDs from hook - this respects all filter combinations (gateway, sensor, valueType)
    const derivedGateways: string[] = Array.from(new Set((data || []).map((r) => r?.gateway?.id).filter(Boolean) as string[])).slice(0, 50);
    // Prefer backend-provided mapping; fallback to mapping derived from current table data
    const sensorsByGateway = (() => {
      const map = new Map<string, Set<string>>();
      if (devicesDir && devicesDir.mapping) {
        for (const [gid, sids] of Object.entries(devicesDir.mapping)) {
          map.set(gid, new Set<string>(sids as string[]));
        }
        return map;
      }
      for (const r of data || []) {
        const sid = r?.sensor?.id;
        const gid = r?.gateway?.id;
        if (!sid) continue;
        if (gid) {
          if (!map.has(gid)) map.set(gid, new Set<string>());
          map.get(gid)!.add(sid);
        }
      }
      return map;
    })();
    const sensorsWithoutGateway: string[] = devicesDir?.sensorsWithoutGateway
      ? devicesDir.sensorsWithoutGateway
      : Array.from(new Set((data || []).filter((r) => r?.sensor?.id && !r?.gateway?.id).map((r) => r.sensor!.id as string))).slice(0, 50);
    // Use filtered sensor IDs from hook if filters are active, otherwise derive from data
    const derivedSensors: string[] = Array.from(new Set((data || []).map((r) => r?.sensor?.id).filter(Boolean) as string[])).slice(0, 50);
    const effectiveSensorIds: string[] = filteredSensorIds.length > 0 ? filteredSensorIds : derivedSensors;
    const effectiveGatewayIds: string[] = gatewayIds.length > 0 ? gatewayIds : derivedGateways;

    async function load() {
      try {
        // Build padded request window to improve edge averages/trends
        const PAD_STEPS = 5;
        const fromMsOrig = from ? Date.parse(from) : undefined;
        const toMsOrig = to ? Date.parse(to) : undefined;
        const fromReq = fromMsOrig ? new Date(Math.max(0, fromMsOrig - PAD_STEPS * stepMs)).toISOString() : from;
        const toReq = toMsOrig ? new Date(toMsOrig + PAD_STEPS * stepMs).toISOString() : to;
        // Per-line fetching
        type LineRequest = { label: string; promise: Promise<{ items: SeriesPoint[] }>; metric: string };
        const requests: LineRequest[] = [];

        // If splitting by gateway, fetch per gateway. If sensors for that gateway exist, fetch per sensor to show all sensors of that gateway.
        // Helper: choose stats per metric based on desired shape
        const statsForLine = (lineKey: string, baseMetric: string): 'avg' | 'minmaxavg' | 'quartiles' => {
          const t = (metricSettings[lineKey]?.type || metricSettings[baseMetric]?.type || 'line');
          if (t === 'boxplot') return 'quartiles';
          if (t === 'arearange') return 'minmaxavg';
          return 'avg'; // line/step/bar
        };

        // Since we don't send metrics to backend anymore, we fetch per sensor
        // Backend returns all metrics for each sensor
        if (effectiveSensorIds.length > 0) {
          // one request per sensor - backend returns all metrics for that sensor
          for (const sid of effectiveSensorIds) {
            // Use first selected metric as label base, or sensor ID if no metrics selected
            const baseLabel = metricsParam.length > 0 ? metricsParam[0] : 'data';
            const label = `${baseLabel} (${sid})`;
            const metric = metricsParam.length > 0 ? metricsParam[0] : 'value';
            requests.push({
              label,
              metric,
              promise: fetchSeries({ bucket, points, resolution: interval, from: fromReq, to: toReq, sensorId: sid, stats: statsForLine(label, metric) }),
            });
          }
        }

        const results = await Promise.all(requests.map((r) => r.promise));
        if (cancelled) return;


        const allTimestamps = new Set<string>();
        results.forEach((r) => (r.items || []).forEach((pt) => allTimestamps.add(pt.timestamp)));
        const merged: SeriesPoint[] = Array.from(allTimestamps)
          .sort()
          .map((ts) => ({ timestamp: ts } as SeriesPoint));
        const byTs = new Map<string, SeriesPoint>(merged.map((p) => [p.timestamp, p]));

        results.forEach((res, idx) => {
          const sensorId = effectiveSensorIds[idx];
          (res.items || []).forEach((pt) => {
            const row = byTs.get(pt.timestamp)!;
            // Extract humidity.general data - this is the main metric we want to display
            // humidity.general contains the actual sensor measurement data
            if (pt['humidity.general'] && typeof pt['humidity.general'] === 'object') {
              // Use valueTypeName for label instead of raw metric name
              const valueTypeName = sensorValueTypeNameById.get(sensorId) || 'humidity.general';
              const metricLabel = `${valueTypeName} (${sensorId})`;
              (row as Record<string, SeriesPoint[string]>)[metricLabel] = pt['humidity.general'] as SeriesPoint[string];
            }
          });
        });

        // Overlay Zusatz-Metriken (Wetter) across all charts, aligned to existing timestamps
        // Re-read from localStorage in case it was updated on SettingsPage in the same tab
        let effectiveWeatherPlace = weatherPlace;
        if (!effectiveWeatherPlace) {
          try {
            const wp = localStorage.getItem('tt_weather_place');
            if (wp) {
              const o = JSON.parse(wp);
              if (o && typeof o === 'object' && 'latitude' in o && 'longitude' in o) {
                effectiveWeatherPlace = o;
                setWeatherPlace(o);
              }
            }
          } catch { /* ignore */ }
        }
        if (effectiveWeatherPlace && weatherMetrics.length > 0) {
          try {
            const w = await fetchWeatherSeries({
              latitude: effectiveWeatherPlace.latitude,
              longitude: effectiveWeatherPlace.longitude,
              from,
              to,
              interval,
              metrics: weatherMetrics,
            });
            const targetTimestamps = Array.from(byTs.keys()).sort();
            // Build arrays of weather points per metric, sorted by timestamp
            const weatherByMetric: Record<string, Array<{ ts: string; val: SeriesPoint[string] }>> = {};
            for (const wm of weatherMetrics) weatherByMetric[wm] = [];
            (w.items || []).forEach((pt) => {
              for (const wm of weatherMetrics) {
                const val = pt[wm] as SeriesPoint[string];
                if (val && typeof val === 'object') {
                  weatherByMetric[wm].push({ ts: pt.timestamp, val });
                }
              }
            });
            // Forward-fill each weather metric onto the chart timestamps
            for (const wm of weatherMetrics) {
              const arr = weatherByMetric[wm].sort((a, b) => a.ts.localeCompare(b.ts));
              let idx = 0;
              let lastVal: SeriesPoint[string] | undefined;
              for (const ts of targetTimestamps) {
                while (idx < arr.length && arr[idx].ts <= ts) {
                  lastVal = arr[idx].val;
                  idx++;
                }
                if (!lastVal) continue; // no value yet
                const row = byTs.get(ts)!;
                (row as Record<string, SeriesPoint[string]>)[wm] = lastVal;
              }
            }
          } catch (e) {
            // Ignore weather overlay errors
          }
        }

        let itemsOut = Array.from(byTs.values());
        // NOTE: All aggregation (including monthly) is now done in the backend
        // Frontend just displays the data as-is
        // Clip back to original [from, to] window if both are defined
        if (from && to) {
          const fMs = Date.parse(from);
          const tMs = Date.parse(to);
          itemsOut = itemsOut.filter((row) => {
            const ms = Date.parse(String(row.timestamp).replace(' ', 'T'));
            return Number.isFinite(ms) && ms >= fMs && ms <= tMs;
          });
        }
        setSeriesItems(itemsOut);
      } catch (e) {
        if (!cancelled) setSeriesItems([]);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [initLoaded, chartMetric, selectedMetrics, metricSettings, query.from, query.to, interval, splitByGateway, weatherPlace, weatherMetrics, filteredSensorIds]);

  const _totalPages = useMemo(() => Math.max(1, Math.ceil(total / (query.pageSize ?? 10))), [total, query.pageSize]);
  // no-op: previously used for sparkline; kept for potential quick stats in future

  // Ensure selected metrics have default settings (default to line)
  useEffect(() => {
    setMetricSettings((prev) => {
      const next = { ...prev };
      selectedMetrics.forEach((m, idx) => {
        if (!next[m]) {
          // simple palette
          const palette = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4'];
          next[m] = { color: palette[idx % palette.length], axis: idx % 2 === 0 ? 'left' : 'right', type: 'line' };
        }
      });
      return next;
    });
  }, [selectedMetrics]);

  // Helper: Determine chart mode based on metrics
  const determineChartMode = (metricsToCheck: string[]): 'line' | 'arearange' | 'boxplot' => {
    const hasBoxplot = metricsToCheck.some((m) => {
      // Try full lineKey first, then fallback to base metric
      return metricSettings[m]?.type === 'boxplot' || metricSettings[m.includes(' (') ? m.substring(0, m.indexOf(' (')) : m]?.type === 'boxplot';
    });
    if (hasBoxplot) return 'boxplot';
    const hasAreaRange = metricsToCheck.some((m) => {
      // Try full lineKey first, then fallback to base metric
      return metricSettings[m]?.type === 'arearange' || metricSettings[m.includes(' (') ? m.substring(0, m.indexOf(' (')) : m]?.type === 'arearange';
    });
    return hasAreaRange ? 'arearange' : 'line';
  };

  // Helper: Build color, axes, units, shapes maps for metrics
  const buildMetricMaps = (metricsToMap: string[]) => {
    const palette = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#06b6d4', '#84cc16', '#f43f5e'];
    const unitForMetric = (label: string): string => {
      // Extract sensor ID from label (format: "metricName (sensorId)")
      const match = label.match(/\(([^)]+)\)$/);
      if (match) {
        const sensorId = match[1];
        const valueTypeValue = sensorValueTypeById.get(sensorId);
        if (valueTypeValue) return valueTypeValue;
      }
      
      // Fallback to unitForAnyMetric
      const base = label.includes(' (') ? label.substring(0, label.indexOf(' (')) : label;
      const u = unitForAnyMetric(base);
      if (u) return u;
      
      return '';
    };
    return {
      colors: Object.fromEntries(metricsToMap.map((k, i) => {
        // Use index-based color from palette as fallback to ensure different colors
        const baseMetric = k.includes(' (') ? k.substring(0, k.indexOf(' (')) : k;
        const userColor = seriesColors[k];
        const settingsColor = metricSettings[k]?.color || metricSettings[baseMetric]?.color;
        const paletteColor = palette[i % palette.length];
        return [k, userColor || settingsColor || paletteColor];
      })),
      axes: Object.fromEntries(metricsToMap.map((label) => {
        // Try full lineKey first, then fallback to base metric
        const ax = (metricSettings[label]?.axis ?? metricSettings[label.includes(' (') ? label.substring(0, label.indexOf(' (')) : label]?.axis ?? 'left') as 'left' | 'right' | 'both';
        return [label, ax];
      })) as Record<string, 'left' | 'right' | 'both'>,
      units: Object.fromEntries(metricsToMap.map((label) => {
        return [label, unitForMetric(label)];
      })) as Record<string, string>,
      shapes: Object.fromEntries(metricsToMap.map((label) => {
        // Try full lineKey first, then fallback to base metric
        const type = metricSettings[label]?.type || metricSettings[label.includes(' (') ? label.substring(0, label.indexOf(' (')) : label]?.type || 'line';
        return [label, type];
      })) as Record<string, 'line' | 'step' | 'boxplot' | 'arearange'>,
    };
  };

  // Helper: Render a single chart with optional title
  const renderChart = (
    metrics: string[],
    overlayMetrics?: string[],
    title?: string,
    height?: number,
  ) => {
    const allMetrics = overlayMetrics ? [...metrics, ...overlayMetrics] : metrics;
    const maps = buildMetricMaps(allMetrics);
    const mode = determineChartMode(metrics);
    const chartHeight = height || (isMobile ? 480 : 640);
    
    const chartElement = (
      <DataChartSection
        mode={mode}
        data={seriesItems}
        metrics={metrics}
        colors={maps.colors}
        axes={maps.axes}
        shapes={maps.shapes}
        units={maps.units}
        resolution={interval}
        dateOnlyTicks={dateOnlyTicks}
        height={chartHeight}
        showLegend={true}
        displayNameMap={Object.fromEntries(allMetrics.map((l) => [l, translateSeriesLabel(l)]))}
        overlayLines={overlayMetrics && overlayMetrics.length > 0 ? {
          metrics: overlayMetrics,
          colors: maps.colors,
          shapes: Object.fromEntries(overlayMetrics.map((m) => [m, (weatherShapes[m] || 'line')])) as Record<string, 'line' | 'step' | 'bar' | 'boxplot' | 'arearange'>,
        } : undefined}
        unitAxisSides={unitAxisSides}
        showTrends={hasAnyTrendline}
        trendTargets={trendTargetsConst}
      />
    );

    return title ? (
      <Stack gap={4}>
        <Text fw={600}>{title}</Text>
        {chartElement}
      </Stack>
    ) : chartElement;
  };

  return (
    <Stack gap="lg">
      <Title order={2}>{t('navigation.data') || 'Daten'}</Title>

      {/* Loading state */}
      {loading && (
        <Card withBorder>
          <Stack align="center" justify="center" style={{ minHeight: 300 }} gap="lg">
            <Loader size="xl" color="blue" />
            
            <Stack gap="sm" align="center" style={{ minWidth: 300 }}>
              <Text size="lg" fw={500}>
                {t('common.loading') || 'Loading...'}
              </Text>
              
              {loadingStage && (
                <Text size="sm" c="dimmed">
                  {loadingStage}
                </Text>
              )}
              
              {loadingProgress > 0 && (
                <Progress 
                  value={loadingProgress} 
                  size="md" 
                  color="blue" 
                  style={{ width: '100%' }}
                  animated
                />
              )}
              
              <Group gap="xs" justify="center">
                <Text size="xs" c="dimmed">
                  {loadingProgress}%
                </Text>
                {loadingProgress === 100 && (
                  <Text size="xs" c="green">
                    ✓ {t('common.loading.ready') || 'Ready'}
                  </Text>
                )}
              </Group>
            </Stack>
          </Stack>
        </Card>
      )}

      {/* Error state */}
      {error && !loading && (
        <Card withBorder>
          <Text c="red" ta="center">{error}</Text>
        </Card>
      )}

      {/* Filter - only show when not loading */}
      {!loading && (
      <Card withBorder p={isMobile ? 'sm' : 'md'} style={{ overflow: 'visible' }}>
        <Stack gap="sm" style={{ minWidth: 0 }}>
          <Group justify="space-between" align="center">
            <Title order={5}>{t('data.filters.title') || 'Filter'}</Title>
            <Group gap="xs">
              <Button size="xs" variant="light" onClick={() => setFiltersOpen((v) => !v)}>
                {filtersOpen ? (t('common.hide') || 'Ausblenden') : (t('common.show') || 'Einblenden')}
              </Button>
            </Group>
          </Group>
          <Collapse in={filtersOpen}>
            <SimpleGrid cols={{ base: 1, md: 3, lg: 3 }} spacing="sm">
              <DateTimePicker
                label={t('data.filters.from') || 'Von'}
                placeholder="Datum & Zeit wählen"
                value={query.from ?? null}
                onChange={(value: string | null) => setQuery((q) => ({ ...q, from: value || undefined, page: 1 }))}
                firstDayOfWeek={1}
                withSeconds={false}
                valueFormat="DD.MM.YYYY HH:mm"
                dropdownType="popover"
                clearable
              />
              <DateTimePicker
                label={t('data.filters.to') || 'Bis'}
                placeholder="Datum & Zeit wählen"
                value={query.to ?? null}
                onChange={(value: string | null) => setQuery((q) => ({ ...q, to: value || undefined, page: 1 }))}
                firstDayOfWeek={1}
                withSeconds={false}
                valueFormat="DD.MM.YYYY HH:mm"
                dropdownType="popover"
                clearable
              />
              <Select
                label={t('data.chart.interval') || 'Intervall'}
                placeholder="Intervall"
                data={intervalOptions}
                value={interval}
                onChange={(v) => v && setInterval(v)}
                comboboxProps={{ withinPortal: true }}
              />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="sm" mt="sm">
              <MultiSelect
                label={t('data.filters.metrics') || 'Metriken'}
                placeholder={t('data.filters.metricPlaceholder') || 'Metriken auswählen'}
                data={deviceFilter.valueTypeOptions}
                value={selectedMetrics}
                onChange={(vals) => {
                  setSelectedMetrics(vals);
                  deviceFilter.setSelectedValueTypes(vals);
                  if (vals && vals.length > 0) {
                    setChartMetric(vals[0]);
                  }
                  // Keep table unfiltered by metric unless needed later
                  setQuery((q) => ({ ...q, metric: undefined, page: 1 }));
                }}
                searchable
                clearable
              />
              <div>
                <MultiSelect
                  label={t('data.filters.weatherMetrics') || 'Zusatz-Metriken (Wetter)'}
                  placeholder={t('data.filters.weatherMetricPlaceholder') || 'Zusatz-Metriken auswählen'}
                  data={[
                    { value: 'weather.temperature', label: t('weather.temperature') || 'Temperatur (Wetter)' },
                    { value: 'weather.humidity', label: t('weather.humidity') || 'Luftfeuchte (Wetter)' },
                    { value: 'weather.precipitation', label: t('weather.precipitation') || 'Niederschlag (Wetter)' },
                  ]}
                  value={weatherMetrics as string[]}
                  onChange={(vals) => setWeatherMetrics(vals as WeatherExtraMetric[])}
                  searchable
                  clearable
                />
                {weatherMetrics.length > 0 && !weatherPlace && (
                  <Text size="xs" c="orange" mt={4}>
                    ⚠ {t('data.filters.weatherNoLocation') || 'Kein Standort konfiguriert.'}{' '}
                    <Text component="a" href="/settings" size="xs" c="orange" td="underline">
                      {t('data.filters.weatherGoToSettings') || 'In den Einstellungen festlegen.'}
                    </Text>
                  </Text>
                )}
              </div>
              {/* Location selection moved to SettingsPage; DataPage reads from localStorage (tt_weather_place). */}
              <MultiSelect
                label={t('data.filters.gateway') || 'Gateway-ID'}
                placeholder="gw-1"
                data={gatewayOptions}
                value={Array.isArray(query.gatewayId) ? query.gatewayId : (query.gatewayId ? [query.gatewayId] : [])}
                onChange={(vals) => {
                  deviceFilter.setSelectedGateways(vals);
                  setQuery((q) => ({ ...q, gatewayId: vals.length ? vals : undefined, page: 1 }));
                }}
                searchable
                searchValue={gatewaySearch}
                onSearchChange={(s) => {
                  setGatewaySearch(s);
                }}
                nothingFoundMessage={t('common.none') || 'Nichts gefunden'}
                rightSection={deviceFilter.loading ? <Loader size="xs" /> : null}
                clearable
              />
              <MultiSelect
                label={t('data.filters.sensor') || 'Sensor-ID'}
                placeholder="sn-1"
                data={sensorOptions}
                value={Array.isArray(query.sensorId) ? query.sensorId : (query.sensorId ? [query.sensorId] : [])}
                onChange={(vals) => {
                  deviceFilter.setSelectedSensors(vals);
                  setQuery((q) => ({ ...q, sensorId: vals.length ? vals : undefined, page: 1 }));
                }}
                searchable
                searchValue={sensorSearch}
                onSearchChange={(s) => {
                  setSensorSearch(s);
                  debouncedSensorSearch(s);
                }}
                nothingFoundMessage={t('common.none') || 'Nichts gefunden'}
                rightSection={sensorLoading ? <Loader size="xs" /> : null}
                clearable
              />
            </SimpleGrid>
            <Group gap="sm" justify="flex-end" mt="sm">
              <Button size="xs" variant="light" onClick={() => setChartSettingsOpen((v) => !v)}>
                {chartSettingsOpen ? (t('common.hide') || 'Ausblenden') : (t('data.chart.settings') || 'Diagrammeinstellungen')}
              </Button>
              <Button variant="default" onClick={() => {
                deviceFilter.resetFilters();
                setSelectedMetrics([]);
                setQuery({ page: 1, pageSize: query.pageSize ?? 10 });
              }}>
                {t('data.filters.reset') || 'Zurücksetzen'}
              </Button>
            </Group>
          </Collapse>
          <Collapse in={chartSettingsOpen}>
            <Card withBorder p="sm" mt="sm">
              <Stack gap="sm">
                <Group gap="md">
                  <Checkbox
                    label={t('data.chart.splitByMetric') || 'Nach Metrik aufteilen'}
                    checked={splitByMetric}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSplitByMetric(e.currentTarget.checked)}
                  />
                  <Checkbox
                    label={t('data.chart.splitByDevice') || 'Nach Gerät aufteilen (Gateway/Sensor)'}
                    checked={splitByDevice}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSplitByDevice(e.currentTarget.checked)}
                  />
                  <Checkbox
                    label={t('data.chart.splitByGateway') || 'Nach Gateway aufteilen'}
                    checked={splitByGateway}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSplitByGateway(e.currentTarget.checked)}
                  />
                </Group>
                {/* Per-series colors */}
                {(() => {
                  const baseMetrics = selectedMetrics.length ? selectedMetrics : (chartMetric ? [chartMetric] : []);
                  const sensorIds: string[] = Array.isArray(query.sensorId)
                    ? (query.sensorId as string[])
                    : (query.sensorId ? [query.sensorId as string] : []);
                  const gatewayIds: string[] = Array.isArray(query.gatewayId)
                    ? (query.gatewayId as string[])
                    : (query.gatewayId ? [query.gatewayId as string] : []);
                  const derivedGateways: string[] = Array.from(new Set((data || []).map((r) => r?.gateway?.id).filter(Boolean) as string[])).slice(0, 10);
                  const derivedSensors: string[] = Array.from(new Set((data || []).map((r) => r?.sensor?.id).filter(Boolean) as string[])).slice(0, 10);
                  const effectiveSensorIds: string[] = sensorIds.length > 0 ? sensorIds : derivedSensors;
                  let displayMetrics = effectiveSensorIds.length >= 1
                    ? effectiveSensorIds.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`))
                    : (gatewayIds.length > 1
                        ? gatewayIds.flatMap((gid) => baseMetrics.map((m) => `${m} (${gid})`))
                        : baseMetrics);
                  if (weatherMetrics.length > 0) displayMetrics = [...displayMetrics, ...weatherMetrics];
                  const palette = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4'];
                  return (
                    <Stack gap={6}>
                      {displayMetrics.map((label, idx) => {
                        const base = label.includes(' (') ? label.substring(0, label.indexOf(' (')) : label;
                        const fallback = metricSettings[label]?.color || metricSettings[base]?.color || palette[idx % palette.length];
                        return (
                          <Group key={`color-${label}`} justify="space-between" align="center">
                            <Text>{translateSeriesLabel(label)}</Text>
                            <ColorInput
                              size="xs"
                              value={seriesColors[label] || fallback}
                              onChange={(val) => setSeriesColors((prev) => ({ ...prev, [label]: val }))}
                              format="hex"
                              withPicker
                              swatches={[
                                '#ef4444','#f97316','#f59e0b','#10b981','#3b82f6','#8b5cf6','#06b6d4','#84cc16','#f43f5e','#a855f7',
                              ]}
                            />
                          </Group>
                        );
                      })}
                    </Stack>
                  );
                })()}
              </Stack>
            </Card>
          </Collapse>
        </Stack>
      </Card>
      )}

      {/* Chart - only show when not loading */}
      {!loading && (
      <Card withBorder p={isMobile ? 'sm' : 'md'} style={{ overflow: 'hidden', maxWidth: '100%' }}>
        <Group justify="space-between" align="center" mb="sm">
          <Group gap="xs" align="center">
            <Title order={4}>{t('data.chart.title') || 'Diagramm'}</Title>
          </Group>
          <Group gap="xs">
            <Tooltip label={t('data.chart.settings') || 'Einstellungen'} withArrow>
              <ActionIcon variant="subtle" onClick={() => setSettingsOpen(true)} aria-label="settings">
                <IconSettings size={36} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        {(() => {
          return seriesItems && seriesItems.length ? (
            (() => {
              const baseMetrics = selectedMetrics.length ? selectedMetrics : (chartMetric ? [chartMetric] : []);
              const sensorIds: string[] = Array.isArray(query.sensorId)
                ? query.sensorId
                : (query.sensorId ? [query.sensorId] : []);
              const gatewayIds: string[] = Array.isArray(query.gatewayId)
                ? query.gatewayId
                : (query.gatewayId ? [query.gatewayId] : []);
              const derivedGateways: string[] = Array.from(new Set((data || []).map((r) => r?.gateway?.id).filter(Boolean) as string[])).slice(0, 10);
              const derivedSensors: string[] = Array.from(new Set((data || []).map((r) => r?.sensor?.id).filter(Boolean) as string[])).slice(0, 10);
              const effectiveSensorIds: string[] = sensorIds.length > 0 ? sensorIds : derivedSensors;
              // Allow boxplot (formerly candlestick) if NOT multiple sensors. Multiple metrics allowed, but we will enforce only one boxplot metric at a time.
              const canCandle = effectiveSensorIds.length <= 1;
              // Labels for boxplot datasets (must match merged keys):
              // - multiple sensors -> labels per sensor (disabled by canCandle)
              // - single gateway selected, no sensors -> aggregate: labels are metric names only
              // - default single sensor -> labels 'metric (sensor)'
              // Enforce only ONE boxplot metric: pick the first metric marked as 'boxplot'
              const candleBase = baseMetrics.find((m) => {
                // Check if any line for this metric has boxplot type
                return Object.keys(metricSettings).some((key) => {
                  const isMatch = key === m || (key.includes(' (') && key.substring(0, key.indexOf(' (')) === m);
                  return isMatch && metricSettings[key]?.type === 'boxplot';
                });
              });
              let _candleMetrics: string[] = [];
              if (canCandle && candleBase) {
                if (effectiveSensorIds.length === 1) {
                  const sid = effectiveSensorIds[0];
                  _candleMetrics = [`${candleBase} (${sid})`];
                } else if (gatewayIds.length === 1 && effectiveSensorIds.length === 0) {
                  _candleMetrics = [candleBase];
                } else if (gatewayIds.length > 1) {
                  _candleMetrics = gatewayIds.map((gid) => `${candleBase} (${gid})`);
                } else {
                  _candleMetrics = [candleBase];
                }
              }
              // Extract actual metric keys from seriesItems (excluding timestamp and weather metrics)
              const actualMetricKeys = new Set<string>();
              seriesItems.forEach((item) => {
                Object.keys(item).forEach((key) => {
                  if (key !== 'timestamp' && typeof item[key] === 'object' && item[key] !== null) {
                    actualMetricKeys.add(key);
                  }
                });
              });
              // Use actual metrics from data, fallback to computed displayMetrics
              let displayMetrics = actualMetricKeys.size > 0 
                ? Array.from(actualMetricKeys).filter(k => !(weatherMetrics as string[]).includes(k))
                : (effectiveSensorIds.length >= 1
                    ? effectiveSensorIds.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`))
                    : (gatewayIds.length > 1
                        ? gatewayIds.flatMap((gid) => baseMetrics.map((m) => `${m} (${gid})`))
                        : baseMetrics));
              if (weatherMetrics.length > 0) displayMetrics = [...displayMetrics, ...weatherMetrics];
              const _anyCandle = !!candleBase;
              

              // Split handling
              // Auto-tile on narrow screens when multiple lines would overlap
              const shouldTile = isMobile;
              if (splitByMetric || shouldTile) {
                // Local unit resolver for this block
                const unitFor = (base: string): string => {
                  const u = unitForMetricId(base);
                  if (u) return u;
                  const b = base.toLowerCase();
                  if (b.includes('humidity') || b.includes('feucht') || b.includes('batter')) return '%';
                  if (b.includes('temp')) return '°C';
                  if (b.includes('press')) return 'hPa';
                  if (b.includes('voltage') || b.includes('volt')) return 'V';
                  if (b.includes('current') || b.includes('amp')) return 'A';
                  if (b.includes('power') || b.includes('leistung') || b.includes('watt')) return 'W';
                  return '';
                };
                // Build colors for MetricChartBlock
                const palette = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4'];
                const colors = Object.fromEntries(displayMetrics.map((k, i) => {
                  const base = k.includes(' (') ? k.substring(0, k.indexOf(' (')) : k;
                  const fallback = metricSettings[k]?.color || metricSettings[base]?.color || palette[i % palette.length];
                  return [k, seriesColors[k] || fallback];
                }));
                return (
                  <MetricChartBlock
                    t={t}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    seriesItems={seriesItems}
                    baseMetrics={baseMetrics}
                    translateMetricId={translateMetricId}
                    translateSeriesLabel={translateSeriesLabel}
                    unitForMetric={unitFor}
                    effectiveSensorIds={effectiveSensorIds}
                    gatewayIds={gatewayIds}
                    metricSettings={metricSettings}
                    weatherMetrics={weatherMetrics as string[]}
                    colors={colors as Record<string, string>}
                    interval={interval}
                    dateOnlyTicks={dateOnlyTicks}
                    unitAxisSides={unitAxisSides}
                    weatherShapes={weatherShapes}
                    showTrends={hasAnyTrendline}
                    trendTargets={trendTargetsConst}
                    canCandle={canCandle}
                    candleBase={candleBase}
                  />
                );
              }

              if (splitByGateway && (gatewayIds.length >= 1 || derivedGateways.length >= 1)) {
                // One chart per gateway; include a separate chart for sensors without a gateway
                const sensorsByGatewayRender = (() => {
                  const map = new Map<string, Set<string>>();
                  for (const r of data || []) {
                    const sid = r?.sensor?.id;
                    const gid = r?.gateway?.id;
                    if (!sid) continue;
                    if (gid) {
                      if (!map.has(gid)) map.set(gid, new Set<string>());
                      map.get(gid)!.add(sid);
                    }
                  }
                  return map;
                })();
                const sensorsNoGw = Array.from(new Set((data || []).filter((r) => r?.sensor?.id && !r?.gateway?.id).map((r) => r.sensor!.id as string)));

                const gwCandidates = (gatewayIds.length > 0 ? gatewayIds : derivedGateways);
                const gwList = gwCandidates.filter((gid) => {
                  const gwSensors = Array.from(sensorsByGatewayRender.get(gid) ?? new Set<string>());
                  if (gwSensors.length > 0) {
                    const labelsAll = gwSensors.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`));
                    return seriesItems.some((pt: SeriesPoint) => labelsAll.some((l) => {
                      const value = (pt as unknown as Record<string, unknown>)[l];
                      return value !== undefined && value !== null;
                    }));
                  }
                  // Fallback: aggregated per gateway
                  const labelsAgg = baseMetrics.map((m) => `${m} (${gid})`);
                  return seriesItems.some((pt: SeriesPoint) => labelsAgg.some((l) => {
                    const value = (pt as unknown as Record<string, unknown>)[l];
                    return value !== undefined && value !== null;
                  }));
                });
                if (gwList.length === 0) {
                  // If no gateways have data, still check if sensors without gateway have data
                  const labelsNoGw = sensorsNoGw.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`));
                  const hasNoGwData = labelsNoGw.length > 0 && seriesItems.some((pt: SeriesPoint) => labelsNoGw.some((l) => {
                    const value = (pt as unknown as Record<string, unknown>)[l];
                    return value !== undefined && value !== null;
                  }));
                  if (!hasNoGwData) {
                    return (
                      <Text size="sm" c="dimmed">
                        {t('common.noData') || 'Keine Daten für die ausgewählten Gateways.'}
                      </Text>
                    );
                  }
                }
                return (
                  <Stack>
                    {gwList.map((gid: string) => {
                      const gwSensors = Array.from(sensorsByGatewayRender.get(gid) ?? new Set<string>());
                      const labelsAll = gwSensors.length > 0
                        ? gwSensors.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`))
                        : baseMetrics.map((m) => `${m} (${gid})`);
                      const overlayForGw = weatherMetrics.length > 0 ? weatherMetrics : [];
                      return (
                        <div key={`gw-${gid}`}>
                          {renderChart(labelsAll, overlayForGw, `Gateway ${gid}`, isMobile ? 560 : 720)}
                        </div>
                      );
                    })}
                    {/* Sensors without a gateway */}
                    {(() => {
                      if (sensorsNoGw.length === 0) return null;
                      const labelsNoGw = sensorsNoGw.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`));
                      const hasNoGwData = labelsNoGw.length > 0 && seriesItems.some((pt: SeriesPoint) => labelsNoGw.some((l) => {
                        const value = (pt as unknown as Record<string, unknown>)[l];
                        return value !== undefined && value !== null;
                      }));
                      if (!hasNoGwData) return null;
                      const overlayNoGw = weatherMetrics.length > 0 ? weatherMetrics : [];
                      return (
                        <div key="nogw">
                          {renderChart(labelsNoGw, overlayNoGw, t('data.table.noGateway') || 'Ohne Gateway', isMobile ? 400 : 560)}
                        </div>
                      );
                    })()}
                  </Stack>
                );
              }

              if (splitByDevice && (effectiveSensorIds.length > 1 || gatewayIds.length > 1)) {
                // One chart per device (sensor or gateway)
                const deviceKeys = effectiveSensorIds.length >= 1 ? effectiveSensorIds : gatewayIds;
                return (
                  <Stack>
                    {deviceKeys.map((dk) => {
                      const labelsAll = baseMetrics.map((m) => `${m} (${dk})`);
                      const overlayForDevice = weatherMetrics.length > 0 ? weatherMetrics : [];
                      const deviceLabel = effectiveSensorIds.length >= 1 ? `Sensor ${dk}` : `Gateway ${dk}`;
                      return (
                        <div key={`dev-${dk}`}>
                          {renderChart(labelsAll, overlayForDevice, deviceLabel, isMobile ? 400 : 560)}
                        </div>
                      );
                    })}
                  </Stack>
                );
              }

              // Standard chart rendering (no split)
              const overlayForChart = weatherMetrics.length > 0 ? weatherMetrics : [];
              const height = isMobile ? 480 : (isTablet ? 600 : 680);
              
              
              return (
                <div style={{ width: '100%', minWidth: 0 }}>
                  {renderChart(displayMetrics, overlayForChart, undefined, height)}
                </div>
              );
            })()
          ) : (
            <Text c="dimmed">{t('data.chart.noData') || 'Keine Daten zum Anzeigen'}</Text>
          );
        })()}
      </Card>
      )}

      {/* Chart Data Table (mirrors the chart series) - only show when not loading */}
      {!loading && seriesItems && seriesItems.length > 0 && (
        <Card withBorder p={isMobile ? 'sm' : 'md'}>
          <Group justify="space-between" align="center" mb="sm">
            <Title order={4}>{t('data.chart.tableTitle') || 'Tabellarische Ansicht (wie im Diagramm)'}</Title>
          </Group>
          {(() => {
            const baseMetrics = selectedMetrics.length ? selectedMetrics : [chartMetric];
            const sensorIds: string[] = Array.isArray(query.sensorId)
              ? query.sensorId
              : (query.sensorId ? [query.sensorId] : []);
            const gatewayIds: string[] = Array.isArray(query.gatewayId)
              ? query.gatewayId
              : (query.gatewayId ? [query.gatewayId] : []);
            // derive effective sensors and gateways from loaded table data to keep parity with charts
            const derivedSensors: string[] = Array.from(new Set((data || []).map((r) => r?.sensor?.id).filter(Boolean) as string[])).slice(0, 50);
            const effectiveSensorIds: string[] = sensorIds.length > 0 ? sensorIds : derivedSensors;
            const derivedGateways: string[] = Array.from(new Set((data || []).map((r) => r?.gateway?.id).filter(Boolean) as string[])).slice(0, 50);
            const effectiveGatewayIds: string[] = gatewayIds.length > 0 ? gatewayIds : derivedGateways;

            // Build sensor grouping by gateway (from table data), plus sensors without gateway
            const sensorsByGatewayRender = new Map<string, Set<string>>();
            const sensorsNoGw: string[] = [];
            (data || []).forEach((r) => {
              const sid = r?.sensor?.id as string | undefined;
              if (!sid) return;
              const gid = (r?.gateway?.id as string | undefined) ?? undefined;
              if (gid) {
                if (!sensorsByGatewayRender.has(gid)) sensorsByGatewayRender.set(gid, new Set<string>());
                sensorsByGatewayRender.get(gid)!.add(sid);
              } else if (!sensorsNoGw.includes(sid)) {
                sensorsNoGw.push(sid);
              }
            });

            // Build grouped columns
            type ColGroup = { key: string; title: string; cols: string[] };
            let groups: ColGroup[] = [];

            if (splitByGateway && effectiveGatewayIds.length >= 1) {
              const gwList = effectiveGatewayIds;
              groups = gwList.map((gid) => {
                const gwSensors = Array.from(sensorsByGatewayRender.get(gid) ?? new Set<string>());
                const cols = gwSensors.length > 0
                  ? gwSensors.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`))
                  : baseMetrics.map((m) => `${m} (${gid})`);
                return { key: gid, title: `Gateway ${gid}`, cols };
              });
              if (sensorsNoGw.length > 0) {
                const colsNoGw = sensorsNoGw.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`));
                if (colsNoGw.length > 0) groups.push({ key: 'nogw', title: t('data.table.noGateway') || 'Ohne Gateway', cols: colsNoGw });
              }
            } else {
              const cols = effectiveSensorIds.length >= 1
                ? effectiveSensorIds.flatMap((sid) => baseMetrics.map((m) => `${m} (${sid})`))
                : (gatewayIds.length > 1
                    ? gatewayIds.flatMap((gid) => baseMetrics.map((m) => `${m} (${gid})`))
                    : baseMetrics);
              groups = [{ key: 'default', title: '', cols }];
            }

            const _allColumns = groups.flatMap((g) => g.cols);

            const _fmtTs = (iso: string) => {
              // respect dateOnlyTicks to shorten
              try {
                const ms = Date.parse(iso);
                if (!Number.isFinite(ms)) return iso;
                const d = new Date(ms);
                return dateOnlyTicks
                  ? d.toLocaleDateString()
                  : d.toLocaleString();
              } catch {
                return iso;
              }
            };
            type Boxplot = { low: number; q1: number; median: number; q3: number; high: number };
            const _hasMedian = (val: unknown): val is Boxplot => {
              return typeof val === 'object' && val !== null && 'median' in (val as Record<string, unknown>) && typeof (val as Record<string, unknown>).median === 'number';
            };
            // Unit resolver for base metric labels
            const _unitForMetric = (base: string): string => {
              const u = unitForMetricId(base);
              if (u) return u;
              const b = base.toLowerCase();
              if (b.includes('humidity') || b.includes('feucht') || b.includes('batter')) return '%';
              if (b.includes('temp')) return '°C';
              if (b.includes('press')) return 'hPa';
              if (b.includes('voltage') || b.includes('volt')) return 'V';
              if (b.includes('current') || b.includes('amp')) return 'A';
              if (b.includes('power') || b.includes('leistung') || b.includes('watt')) return 'W';
              return '';
            };

            return (
              <DataTableSection
                t={t as unknown as (k: string, opts?: unknown) => string}
                seriesItems={seriesItems}
                data={data}
                query={query}
                baseMetrics={baseMetrics}
                weatherMetrics={weatherMetrics as string[]}
                dateOnlyTicks={dateOnlyTicks}
                isMobile={isMobile}
                translateSeriesLabel={translateSeriesLabel}
                unitForMetricId={unitForMetricId}
                sensorValueTypeById={sensorValueTypeById}
              />
            );
          })()}
        </Card>
      )}

      {/* Chart Settings Dialog - only show when not loading */}
      {!loading && (
      <DataChartSettingsDialog
        opened={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        t={t}
        query={query}
        data={data}
        selectedMetrics={selectedMetrics}
        metricSettings={metricSettings}
        setMetricSettings={setMetricSettings}
        splitByMetric={splitByMetric}
        setSplitByMetric={setSplitByMetric}
        splitByDevice={splitByDevice}
        setSplitByDevice={setSplitByDevice}
        splitByGateway={splitByGateway}
        setSplitByGateway={setSplitByGateway}
        weatherMetrics={weatherMetrics}
        weatherShapes={weatherShapes}
        setWeatherShapes={setWeatherShapes}
        translateMetricId={translateMetricId}
        availableUnits={availableUnits}
        unitAxisSides={unitAxisSides}
        setUnitAxisSides={setUnitAxisSides}
        showTrends={showTrends}
        setShowTrends={setShowTrends}
      />
      )}
    </Stack>
  );
}

export default DataPage;
